import { Container, Sprite, Texture, Ticker, filters } from "pixi.js"

// Warm amber, the same family as the command wheel's rim (WHEEL_RIM_COLOR), so the hover cue and the
// wheel it can open read as one system.
const GLOW_COLOR = 0xf7c070
// The outline is built by drawing the frame's shape a second time, offset in every direction: the
// union of those offsets sticks out past the art by `distance` on every side, and the art drawn on
// top hides the middle, leaving a rim. Two passes give the rim a falloff -- a tight bright edge and a
// wider faint bloom -- rather than one hard band. Kept deliberately thin and low-contrast: this is a
// "you can act on this" hint the eye should catch without the entity looking like it is on fire.
const OUTLINE_PASSES = [
    { distance: 1.5, alpha: 0.66 },
    { distance: 3.5, alpha: 0.21 },
]
// Eight directions: the four sides plus the diagonals at 1/sqrt(2), so the rim stays an even
// thickness around a curve instead of bulging at the corners.
const DIAGONAL = Math.SQRT1_2
const OUTLINE_DIRECTIONS: [number, number][] = [
    [1, 0],
    [-1, 0],
    [0, 1],
    [0, -1],
    [DIAGONAL, DIAGONAL],
    [-DIAGONAL, DIAGONAL],
    [DIAGONAL, -DIAGONAL],
    [-DIAGONAL, -DIAGONAL],
]

// Breathing, not blinking: the glow rides between PULSE_FLOOR and full strength over PULSE_PERIOD_MS.
const PULSE_PERIOD_MS = 1600
const PULSE_FLOOR = 0.62

// The glow eases in and out rather than snapping, so sweeping the pointer across a crowd doesn't
// strobe. Out is slower than in: the arrival should feel responsive, the departure unhurried.
const FADE_IN_MS = 140
const FADE_OUT_MS = 220


// The outline passes are drawn from the entity's OWN frame texture, whose edge pixels are the art's
// dark LPC outline -- tinting those would give a dark halo, not a glow. This flattens every pass to
// the glow color while keeping its alpha, so what shows is the shape, lit. One filter, shared by
// every highlight, and only ever one entity is highlighted at a time.
let glowFlattenFilter: any

const flattenToGlowColor = () => {
    if (!glowFlattenFilter) {
        const r = ((GLOW_COLOR >> 16) & 0xff) / 0xff
        const g = ((GLOW_COLOR >> 8) & 0xff) / 0xff
        const b = (GLOW_COLOR & 0xff) / 0xff
        const filter = new filters.ColorMatrixFilter()
        // Zero every color coefficient and carry the glow color in the offset column; the alpha row
        // passes alpha straight through, so the silhouette's shape survives untouched.
        filter.matrix = [0, 0, 0, 0, r, 0, 0, 0, 0, g, 0, 0, 0, 0, b, 0, 0, 0, 1, 0]
        glowFlattenFilter = filter
    }
    return glowFlattenFilter
}

// The highlight is pure decoration and must never move the thing it decorates. Renderables derive
// their on-screen position from their own measured height (BaseRenderable.getYOffset reads
// `this.height`, which is a bounds query over every child), so a decoration that widened the parent's
// bounds would nudge the entity the instant you pointed at it -- and nudge it back when you looked
// away. This container renders its children normally but reports no bounds of its own, which keeps
// the whole highlight out of every measurement taken of the entity.
//
// A filter still sizes itself correctly inside this: the filter system asks the FILTERED object for
// its bounds directly (FilterSystem.push -> target.getBounds), never through its parent.
class BoundlessContainer extends Container {
    calculateBounds() {
        this._bounds.clear()
    }
}

// The current frame of whatever a renderable is drawing, in that renderable's own art space.
export interface HighlightFrame {
    texture: Texture
    x: number
    y: number
    scaleX: number
    scaleY: number
}

// One frame, or several: an entity drawn from map tiles (the V2 chest is two stacked tile sprites)
// is outlined as the union of all of them, so the glow follows the object's real shape.
export type HighlightFrameProvider = () => HighlightFrame | HighlightFrame[] | null | undefined

export interface EntityHighlightOpts {
    // Where the outline goes: the container the art itself lives in, so the outline inherits the art's
    // transform. It is inserted at index 0, i.e. behind the art -- which is what turns the dilated
    // shape into a rim instead of a silhouette pasted over the entity.
    artContainer?: Container
    frame?: HighlightFrameProvider
    // Depth for the outline inside a z-sorted artContainer (the map surface sorts its tile sprites by
    // zIndex, so "added first" isn't enough to stay behind them).
    zIndex?: () => number
}

// Hover feedback for an entity that has interactions: no icons and no new click targets, just the
// entity picking up a glowing edge. Two shapes, because entities come in two kinds:
//
//   - anything drawing a sprite gets a POSE-ACCURATE OUTLINE GLOW traced from the frame it is showing
//     this instant, so it follows the animation. It reuses the baked frame texture the sprite already
//     has (no extra bake, no VRAM).
//   - an entity whose art lives in the TILEMAP -- the V2 chest is an invisible 32x32 marker standing
//     on a tile-drawn chest -- is outlined from those map tiles instead, so it gets exactly the same
//     glow as everything else (see EntityRenderingManager.tileHighlightFrames).
//
// Only the hovered (or, on touch, the focused) entity is ever highlighted, so the per-frame update is
// added to the shared ticker on the way in and removed on the way out instead of running for every
// entity on screen.
export class EntityHighlight {
    private opts: EntityHighlightOpts
    private outline?: Container
    private shell?: BoundlessContainer
    private outlineSprites: Sprite[] = []
    private on: boolean = false
    // 0..1, eased toward `on` every frame; the glow's alpha is this times the pulse. A fade-out keeps
    // ticking until it reaches 0, which is also when the highlight finally hides itself.
    private strength: number = 0
    private ticking: boolean = false
    private disposed: boolean = false

    constructor(opts: EntityHighlightOpts) {
        this.opts = opts
    }

    isOn = () => this.on

    set = (on: boolean) => {
        if (this.disposed || this.on === on) {
            return
        }
        this.on = on
        // Both directions are animated, so switching off keeps the ticker running until the fade
        // finishes -- update() hides everything and deregisters once the glow reaches zero.
        if (!this.ticking) {
            Ticker.shared.add(this.update)
            this.ticking = true
        }
        this.update()
    }

    // Release the ticker registration and the borrowed frame texture. Called from the renderable's
    // cleanup so a culled or destroyed entity can't leave a callback running against a detached
    // display object, or hold a reference the sprite generator wants to reclaim.
    dispose = () => {
        this.disposed = true
        this.on = false
        this.strength = 0
        this.stopTicking()
        this.hide()
    }

    private hide = () => {
        this.hideOutline()
    }

    private stopTicking = () => {
        if (this.ticking) {
            Ticker.shared.remove(this.update)
            this.ticking = false
        }
    }

    private hideOutline = () => {
        if (!this.outline) {
            return
        }
        this.outline.visible = false
        this.outlineSprites.forEach(sprite => {
            sprite.texture = Texture.EMPTY
        })
    }

    // Step `strength` toward the on/off target using real elapsed time, so the fade takes the same
    // wall-clock duration whatever the frame rate.
    private advanceFade = () => {
        const elapsed = Ticker.shared.deltaMS || 0
        const target = this.on ? 1 : 0
        const duration = this.on ? FADE_IN_MS : FADE_OUT_MS
        const step = duration > 0 ? elapsed / duration : 1
        if (this.strength < target) {
            this.strength = Math.min(target, this.strength + step)
        } else if (this.strength > target) {
            this.strength = Math.max(target, this.strength - step)
        }
    }

    // 0..1, a sine that never reaches zero: the glow breathes rather than blinking out. Driven from
    // the wall clock so every highlight pulses in step and the rate doesn't follow the frame rate.
    private pulse = (): number => {
        const phase = (Date.now() % PULSE_PERIOD_MS) / PULSE_PERIOD_MS
        const wave = 0.5 - 0.5 * Math.cos(phase * Math.PI * 2)
        return PULSE_FLOOR + (1 - PULSE_FLOOR) * wave
    }

    private update = () => {
        this.advanceFade()
        if (this.strength <= 0) {
            // Fully faded out (or never faded in): nothing to draw, and nothing left to animate.
            this.hide()
            this.stopTicking()
            return
        }
        if (!this.drawOutline(this.pulse() * this.strength)) {
            this.hideOutline()
        }
    }

    private drawOutline = (pulse: number): boolean => {
        const { artContainer, frame } = this.opts
        if (!artContainer || !frame) {
            return false
        }
        const current = frame()
        const frames = (Array.isArray(current) ? current : [current]).filter(next => next?.texture?.valid)
        if (frames.length === 0) {
            return false
        }

        if (!this.outline) {
            const shell = new BoundlessContainer()
            const outline = new Container()
            outline.filters = [flattenToGlowColor()]
            this.outline = outline
            this.shell = shell
            shell.addChild(outline)
            // Behind the art. LPC animations are appended to their container as they finish baking, so
            // index 0 keeps the outline below whichever frames arrive later.
            artContainer.addChildAt(shell, 0)
        }

        // One copy per (frame x pass x direction). The pool grows to fit and is reused; anything left
        // over from a frame count that has since shrunk is parked empty rather than destroyed.
        let i = 0
        frames.forEach(({ texture, x, y, scaleX, scaleY }) => {
            OUTLINE_PASSES.forEach(pass => {
                OUTLINE_DIRECTIONS.forEach(([dx, dy]) => {
                    const sprite = this.outlineSpriteAt(i++, pass.alpha)
                    sprite.texture = texture
                    sprite.scale.set(scaleX, scaleY)
                    sprite.x = x + dx * pass.distance
                    sprite.y = y + dy * pass.distance
                    sprite.visible = true
                })
            })
        })
        for (let spare = i; spare < this.outlineSprites.length; spare++) {
            this.outlineSprites[spare].visible = false
        }

        this.outline.alpha = pulse
        this.outline.visible = true
        if (this.opts.zIndex && this.shell) {
            this.shell.zIndex = this.opts.zIndex()
        }
        return true
    }

    private outlineSpriteAt = (index: number, alpha: number): Sprite => {
        let sprite = this.outlineSprites[index]
        if (!sprite) {
            sprite = new Sprite(Texture.EMPTY)
            sprite.anchor.set(0, 0)
            this.outlineSprites[index] = sprite
            this.outline.addChild(sprite)
        }
        sprite.alpha = alpha
        return sprite
    }

}
